import React, { forwardRef, useEffect, useState, useRef } from "react";
import Select, { FormatOptionLabelMeta, GroupBase, MultiValue, SingleValue, SelectInstance } from "react-select";
import { fetchContacts } from "../../../api";
import { Contact, ContactType } from "../../../types";
import { ContactForm } from "./ContactForm";
import CustomModal from "../../widgets/components/CustomModal";
import LabelWithTooltip from "../../widgets/components/CustomLabel";
import { mergeSelectStyles } from "../../widgets/components/SelectStyles";
import { getContactDisplayName } from "./ContactHelpers";
import { formatCurrency } from "../../reports/components/ReportHelpers";

interface OptionType {
  value: string;
  label: string;
  otherText: string;
  // MBAI-519: receivable/payable balance carried through from the contact list fetch,
  // shown next to each option so the picker surfaces outstanding balances.
  receivableAmt?: number;
  payableAmt?: number;
  currId?: string;
}

interface ContactPickerProps {
  contactType?: ContactType;
  tabIndex: number;
  label?: string;
  tooltip?: string;
  onContactChange: (contactId: string | string[], contactInfo?: Contact) => void;
  selectedContact?: string | string[];
  onInputChange?: (input: string) => void;
  inputRef?: React.Ref<HTMLInputElement>; // Optional input ref for HTML input
  isDisabled?: boolean,
  selectRef?: React.Ref<SelectInstance<OptionType, boolean, GroupBase<OptionType>>>; // Optional ref for react-select
  isMulti?: boolean; // Optional isMulti prop
  error?: string; // Validation error message
}

export const ContactPicker = forwardRef<HTMLDivElement, ContactPickerProps>(
  (
    {
      contactType,
      tabIndex,
      onContactChange,
      label,
      tooltip,
      selectedContact,
      isMulti = false,
      onInputChange,
      isDisabled,
      inputRef, // Optional input ref for standard input element
      selectRef, // Optional ref for react-select
      error,
    },
    ref
  ) => {
    const [contacts, setContacts] = useState<OptionType[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<SingleValue<OptionType> | MultiValue<OptionType>>(null);
    const [contactList, setContactList] = useState<Contact[]>([]);
    const internalSelectRef = useRef<SelectInstance<OptionType, boolean, GroupBase<OptionType>>>(null); // Internal ref for focus management
    const [textInput, setTextInput] = useState('')
    // Latest callback without making it an effect dependency (parents pass inline closures)
    const onContactChangeRef = useRef(onContactChange);
    onContactChangeRef.current = onContactChange;
    useEffect(() => {

      const getContacts = async () => {
        try {
          // MBAI-519: pass balanceDue=true so the list payload includes each contact's
          // receivableAmt/payableAmt (same fetchContacts() list endpoint ViewContacts.tsx
          // already uses for its Balance column — the API omits these fields otherwise).
          const fetchedContacts: Contact[] = await fetchContacts(true);
          const contactsArray = Array.isArray(fetchedContacts) ? fetchedContacts : [fetchedContacts];


          const options: OptionType[] = contactsArray
            .filter(contact => {
              // If contactType is undefined, include all contacts
              return contactType ? contact?.contactTyp?.includes(contactType) : true;
            })
            .map(contact => ({
              value: contact.contactId || '',
              label: getContactDisplayName(contact) || contact.workPhone || '',
              otherText: JSON.stringify(contact),
              receivableAmt: contact.receivableAmt,
              payableAmt: contact.payableAmt,
              currId: contact.currId,
            }));


          setContacts(options);
          setContactList(contactsArray);

          if (selectedContact) {

            if (isMulti) {
              const defaultOptions = options.filter(option =>
                (selectedContact as string[]).includes(option.value)
              );

              setSelectedOption(defaultOptions.length > 0 ? defaultOptions : null);
            } else {
              // Handle case where selectedContact might be an array even in single select mode
              const contactId = Array.isArray(selectedContact) ? selectedContact[0] : selectedContact;


              const defaultOption = options.find(option => option.value === contactId);

              if (defaultOption) {
                setSelectedOption(defaultOption);
              } else if (typeof contactId === 'string' && contactId !== '') {
                // Try to find by label as fallback (e.g. one-off name stored in contactName)
                const optionByLabel = options.find(option =>
                  option.label === contactId
                );
                // The id may belong to a contact excluded by the contactType filter above (e.g. a
                // contact created by upload without the expected type) — still show its name, not the raw id.
                const contactById = contactsArray.find(c => c.contactId === contactId);
                const optionById: OptionType | undefined = contactById && {
                  value: contactById.contactId || '',
                  label: getContactDisplayName(contactById) || contactById.workPhone || '',
                  otherText: JSON.stringify(contactById),
                  receivableAmt: contactById.receivableAmt,
                  payableAmt: contactById.payableAmt,
                  currId: contactById.currId,
                };
                // If still no match, treat the value as a one-off/free-text name
                const resolvedOption = optionByLabel || optionById;
                setSelectedOption(resolvedOption || { value: contactId, label: contactId, otherText: '' });
                // Records saved with only a contact name (e.g. bulk upload) have no contactId, which the
                // txn/payment forms require. Now that the name resolved to a real contact, hand the id back.
                if (optionByLabel) {
                  const matched = contactsArray.find(c => c.contactId === optionByLabel.value);
                  onContactChangeRef.current(optionByLabel.value, matched);
                }
              } else {
                setSelectedOption(null);
              }
            }
          } else {
            setSelectedOption(null);
          }
        } catch (error) {
          console.error(`❌ Error fetching ${contactType}:`, error);
        }
      };

      getContacts();
    }, [contactType, selectedContact, isMulti]);

    // Add this log after setSelectedOption to see what was actually set
    useEffect(() => {
    }, [selectedOption]);

    useEffect(() => {
      // Focus the select component on mount
      if (internalSelectRef.current && ref) {
        internalSelectRef.current.focus();
      }
    }, []);

    const customStyles = mergeSelectStyles({
      option: (provided: any) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
      }),
      singleValue: (provided: any) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
      }),
      control: (provided: any) => ({
        ...provided,
        // !important needed: react-select's own SCSS beats this emotion class on
        // specificity otherwise, see AccountPicker.tsx (MBAI-160).
        borderColor: error ? '#f1416c !important' : provided.borderColor,
        '&:hover': {
          borderColor: error ? '#f1416c !important' : provided['&:hover']?.borderColor,
        },
      }),
    });

    // Custom filtering based on itemName or itemTxt
    const filterOption = (
      option: { label: string; data: OptionType },
      inputValue: string
    ) => {
      // Always keep the "Add New" quick-create option visible, even when the typed text matches no contact
      if (option.data?.value === 'add_new') return true;
      const labelMatches = option.label.toLowerCase().includes(inputValue.toLowerCase());
      const descriptionMatches = option.data?.otherText?.toString().toLowerCase().includes(inputValue.toLowerCase());
      return labelMatches || descriptionMatches ? true : false; // Always return a boolean
    };

    const formatOptionLabel = (
      { label, receivableAmt, payableAmt, currId }: OptionType,
      meta: FormatOptionLabelMeta<OptionType>
    ) => {
      const context = meta?.context;
      // MBAI-519: show the contact's outstanding receivable/payable balance next to
      // each option in the dropdown list (kept out of the compact selected value).
      const balance = (receivableAmt ?? 0) !== 0 ? (receivableAmt ?? 0) : (payableAmt ?? 0) !== 0 ? -(payableAmt ?? 0) : 0;
      const showBalance = context === 'menu' && balance !== 0;

      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>{label}</span>
          {showBalance && (
            <span
              className={`ms-2 fs-8 fw-bold ${balance > 0 ? 'text-success' : 'text-danger'}`}
              title={(balance > 0 ? 'Receivable'.toTranslatedDisplay() : 'Payable'.toTranslatedDisplay())}
            >
              {formatCurrency(Math.abs(balance), currId)}
            </span>
          )}
        </div>
      );
    };

    const handleSelectChange = (selectedOption: SingleValue<OptionType> | MultiValue<OptionType>) => {
      setTextInput(''); // Add this line at the start

      if (isMulti) {
        const optionsArray = selectedOption as MultiValue<OptionType>;
        if (optionsArray.some(option => option.value === 'add_new')) {
          setIsModalOpen(true);
        } else {
          setSelectedOption(selectedOption);
          const optionSelected = optionsArray.map(option => option.value);
          const selectedContact = contactList.find(contact => contact.contactId === optionSelected[0]);
          onContactChange(optionsArray.map(option => option.value), selectedContact);
        }
      } else {
        const option = selectedOption as SingleValue<OptionType>;
        if (option && option.value === 'add_new') {
          setIsModalOpen(true);
        } else {
          setSelectedOption(selectedOption);
          const matchedContact = contactList.find(contact => contact.contactId === option?.value);
          if (!matchedContact && option === null && onInputChange) {
            // Clearing a one-off (synthetic) value — notify caller
            onInputChange('');
          }
          onContactChange(option?.value || '', matchedContact);
        }
      }
    };

    const handleModalClose = () => {
      setIsModalOpen(false);
    };

    const handleInputChange = (input: string, actionMeta: { action: string }) => {
      if (actionMeta.action === 'input-change') {
        setTextInput(input);
        if (onInputChange) {
          onInputChange(input);
        }
        // Clear selected option when user starts typing
        // if (input && selectedOption) {
        //   setSelectedOption(null);
        //   onContactChange('');
        // }
        return input
      } else if (actionMeta.action === 'input-blur' && textInput && !selectedOption) {
        // Persist the typed one-off value as a synthetic selected option so it
        // remains visible in the field after the user tabs/clicks away.
        setSelectedOption({ value: textInput, label: textInput, otherText: '' });
        setTextInput('');
      }
    };

    const handleAddNew = (newOption: OptionType) => {
      setIsModalOpen(false);
      setTextInput(''); // Add this line
      setContacts(prevContacts => [...prevContacts, newOption]);
      if (isMulti) {
        setSelectedOption(prevOptions => ([...(prevOptions as MultiValue<OptionType>), newOption]));
        onContactChange([...(selectedOption as MultiValue<OptionType>).map(option => option.value), newOption.value]);
      } else {
        setSelectedOption(newOption);
        onContactChange(newOption.value);
      }
    };

    const modifiedOptions = [
      { value: 'add_new', label: 'Add New'.toTranslatedDisplay(), otherText: '' },
      ...contacts,
    ];

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" && textInput.length > 0) {
        const inputValue = textInput
        const filteredOptions = contacts.filter(option =>
          option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.otherText.toLowerCase().includes(inputValue.toLowerCase())
        );
        if (filteredOptions.length === 0) {
          setIsModalOpen(true); // Open the modal if no filtered options are available
        }
      }
    };

    return (
      <div ref={ref} className="searchable-picker">
        <LabelWithTooltip
          className="form-label"
          labelText={label || contactType || 'Contact'}
          tooltipText={tooltip}
        />
        <Select
          inputValue={textInput}
          isMulti={isMulti}
          ref={selectRef || internalSelectRef} // Use the forwarded ref or fallback to internal ref
          className="react-select-styled react-select"
          classNamePrefix="react-select"
          options={modifiedOptions}
          formatOptionLabel={formatOptionLabel}
          styles={customStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          placeholder={`${'Select'.toTranslatedDisplay()} ${(contactType || 'Contact').toTranslatedDisplay()}`}
          value={selectedOption}
          onChange={handleSelectChange}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          tabIndex={tabIndex}
          isDisabled={isDisabled}
          filterOption={filterOption} // Custom filtering logic
          isClearable={true} // Allow clearing the selection
          tabSelectsValue={false}
        />
        {error && <div className="invalid-feedback d-block">{error}</div>}
        <CustomModal
          title={`${'Add New'.toTranslatedDisplay()} ${(contactType || '').toTranslatedDisplay()}`}
          isVisible={isModalOpen}
          onClose={handleModalClose}
          body={<ContactForm contactType={contactType} onClose={handleModalClose} onSave={(contact) => {
            onContactChange(contact.contactId)
            setSelectedOption({ value: contact.contactId, label: getContactDisplayName(contact) || '', otherText: '' })
            handleModalClose()
          }} quickCreate contactName={textInput} modalState={isModalOpen} />}
          id={''}
          size="large"
          onSave={() => { }}
        />
      </div>
    );
  }
);
